/* eslint-disable no-buffer-constructor */
/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { post } from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { MDBCol, MDBRow } from 'mdbreact';
import _ from 'lodash';
import moment from 'moment';

import { actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import FeaturedText from 'components/FeaturedText';
import Footer from 'components/Footer';
import Overlay from 'components/Informations/Overlay';
import ReturnPreviousPage from 'components/ReturnPreviousPage';
import Toast from 'components/Informations/Toast';

import DateField from 'components/FormGroup/DateField';
import ErrorMessage from 'components/FormGroup/ErrorMessage';
import FilterField from 'components/FormGroup/FilterField';
import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';
import Switch from 'components/FormGroup/Switch';

import Accordion from 'components/Accordion';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import { formatDate } from 'utils/moment';
import EditAllocatedInfos from 'pages/CoordinateProjects/InternalComponents/EditAllocatedProfessional';

import { sortArrayInAlphabeticalOrder } from 'utils/objectManipulations';
import {
  handleCoordinatorDelete,
  handleCoordinatorInsert,
  handleCoordinatorUpdate,
} from '../../utils';
import * as S from './styles';

const validationSchema = Yup.object().shape({
  accountManager: Yup.string(),
  clientID: Yup.string().required('Selecione o cliente favorecido'),
  clientRepresentative: Yup.string(),
  expectedInitialDate: Yup.string(),
  projectStatus: Yup.string(),
  coustCenter: Yup.string().required('Selecione o centro de custos do projeto'),
  projectCategory: Yup.string().required('Selecione a categoria do projeto'),
  projectManager: Yup.string().required('Selecione um gerente de projetos'),
  projectName: Yup.string().required('Escreva o nome do projeto'),
  realFinalDate: Yup.string(),
  realInitialDate: Yup.string(),
  technicalResponsible: Yup.string(),
});

const {
  getEmployeesToSelect,
  statusCatProjeto,
  getClients,
  updateProject,
  getAllocatedEmployees,
  deleteAllocation,
  getCoustCenterOptions,
} = endpoints;

function ManageProject({ location, history, user }) {
  const { state } = location;
  const { id_funcionario: idFuncionario, administrador } = user;
  const isAdmin = administrador === 1;

  const [employeesSearched, setEmployeesSearched] = useState('');

  const [isLoading, setIsLoading] = useState(true);

  const [projectName, setProjectName] = useState('');

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');
  const [overlayAction, setOverlayAction] = useState(() => {});

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastType, setToastType] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastClose, setToastClose] = useState(2000);

  const [formAlterations, setFormAlterations] = useState(false);
  const [selectOptions, setSelectOptions] = useState({});

  const [removeEmployee, setRemoveEmployee] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [reloadEmployees, setReloadEmployees] = useState(false);

  const [coordinatorsList, setCoordinatorsList] = useState([
    { id_funcionario_coordenador: -1 },
  ]);

  const [accordionOpen, setAccordionOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [isUniversal, setIsUniversal] = useState(false);
  const [isClientAttendance, setIsClientAttendance] = useState(false);

  const [clientsList] = useFetch(getClients);
  const [employeesList] = useFetch(getEmployeesToSelect);
  const [statusCategoriaList] = useFetch(statusCatProjeto);
  const [allocatedEmployees] = useFetch(
    `${getAllocatedEmployees}/${state.id_projeto}`,
    reloadEmployees,
  );
  const [getCoustCenters] = useFetch(getCoustCenterOptions);

  const initialValues = {
    projectName: state.nome,
    clientID: state.id_cliente,
    projectStatus: state.id_status_projeto,
    technicalResponsible: state.coordenadores?.map(
      ({ id_funcionario_coordenador: idCoordenador }) =>
        ({ id_funcionario: idCoordenador } || []),
    ),
    projectManager: state.id_func_gp_smtx,
    accountManager: state.id_acc_manager,
    projectCategory: state.id_categoria_projeto,
    coustCenter: state.id_centro_de_custo || '',
    clientRepresentative: state.responsavel || '',
    expectedInitialDate: formatDate(state.dt_inicio_prevista, 'YYYY-MM-DD'),
    expectedFinalDate: formatDate(state.dt_final_prevista, 'YYYY-MM-DD'),
    realInitialDate: formatDate(state.dt_inicio, 'YYYY-MM-DD'),
    realFinalDate: formatDate(state.dt_final, 'YYYY-MM-DD'),
  };

  const filterEmployees = () => {
    const filtered =
      employees &&
      employees.filter(
        employee =>
          employee._nome
            .toLowerCase()
            .indexOf(employeesSearched.toLowerCase()) >= 0,
      );

    return setFilteredEmployees(filtered);
  };

  useEffect(() => {
    const {
      coordenadores,
      atendimento_ao_cliente: atendimento,
      projeto_universal: projetoUniversal,
    } = state;

    const coordinatorIds = coordenadores?.map(
      ({ id_funcionario_coordenador: idCoordenador }) => ({
        id_funcionario_coordenador: idCoordenador,
      }),
    );

    setIsUniversal(projetoUniversal === 1);
    setIsClientAttendance(atendimento === 1);
    setCoordinatorsList(coordinatorIds);
  }, [state]);

  useEffect(() => {
    const clients = clientsList
      ?.filter(client => client.id_status_cliente === 1)
      .map(client => ({ value: client.id_cliente, label: client.nome }));

    const withoutRecourse = employeesList?.filter(
      employee => employee.nome.toLowerCase() === 'sem recurso',
    )[0];

    const findEmployeesByFunctionName = (functionName, variantName) =>
      employeesList
        .filter(({ nome_funcao }) => {
          const name = nome_funcao.toLowerCase();
          return name.includes(functionName) || name.includes(variantName);
        })
        ?.map(employee => ({
          value: employee.id_funcionario,
          label: employee.nome,
        }));

    const isGP = employeesList
      .filter(employee => !!employee.is_gp || employee.is_gp !== "b'\\x00'")
      ?.map(employee => ({
        value: employee.id_funcionario,
        label: employee.nome,
      }));

    const allCoordinators = findEmployeesByFunctionName('coordenador');
    const allAccountManagers = findEmployeesByFunctionName('account');
    const allTechLeads = findEmployeesByFunctionName('lead');
    // const allScrumMasters = findEmployeesByFunctionName('scrum');
    // const allDeliveryManagers = findEmployeesByFunctionName('delivery');
    // const allDirectors = findEmployeesByFunctionName('diretor');
    // const allProjectManagers = findEmployeesByFunctionName(
    //   'gerente de projetos',
    // );

    setSelectOptions(prev => ({
      ...prev,
      technical: [...allTechLeads, ...allCoordinators],
      projectManagers: [...isGP],
      clients,
      accountManagers: [
        ...allAccountManagers,
        {
          label: withoutRecourse?.nome,
          value: withoutRecourse?.id_funcionario,
        },
      ],
    }));
  }, [employeesList, clientsList]);

  useEffect(() => {
    if (!state) {
      history.push('/auth/coordinate-projects/view-project', state.index);
      return;
    }
    setIsLoading(true);

    (async () => {
      const statusCategory = await statusCategoriaList;
      const { categoriaProjeto, statusProjeto } = statusCategory;
      const coustCenters = await getCoustCenters;

      const categories =
        categoriaProjeto &&
        categoriaProjeto.map(item => ({
          value: item.id_categoria_projeto,
          label: item.nome_categoria_projeto,
        }));

      const status =
        statusProjeto &&
        statusProjeto.map(item => ({
          value: item.id_status_projeto,
          label: item.tipo_status,
        }));

      const coustCentersData = await coustCenters.map(coust => ({
        value: coust.id,
        label: coust.label,
      }));

      if (status && status.length && categories && categories.length) {
        setIsLoading(false);
        setSelectOptions(prev => ({
          ...prev,
          coustCenters: coustCentersData,
          categories,
          status,
        }));
      }

      const orderedEmployees = allocatedEmployees
        ? _.orderBy(allocatedEmployees, ['_dt_final_alocacao'], ['desc'])
        : [];
      setEmployees(orderedEmployees);

      return () => {
        setToastType('');
        setToastVisibility(false);
        setToastMessage('');
        setToastClose(2000);
      };
    })();
  }, [
    employeesList,
    clientsList,
    statusCategoriaList,
    history,
    state,
    allocatedEmployees,
    getCoustCenters,
  ]);

  const renderToast = useCallback(
    (type, message, closeTime) => {
      const condition = closeTime !== undefined ? closeTime : toastClose;

      setToastVisibility(true);
      setToastMessage(message);
      setToastType(type);
      setToastClose(condition);

      setTimeout(() => {
        setToastVisibility(false);
        setToastMessage('');
        setToastType('');
      }, condition + 1500);
    },
    [toastClose],
  );

  useEffect(() => {
    const toast =
      localStorage.getItem('successToast') !== null || undefined
        ? JSON.parse(localStorage.getItem('successToast'))
        : {};

    if (Object.keys(toast).length) {
      const {
        toastType: type,
        toastMessage: message,
        toastCloseTime: closeTime,
      } = toast;

      renderToast(type, message, closeTime);
    }

    return () => {
      localStorage.removeItem('successToast');
    };
  }, [renderToast]);

  const renderOverlay = type => {
    switch (type) {
      case 'cancel':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cancelando</span>,
              ' a atualização e isso fará com que todas as informações ',
              <span>não salvas</span>,
              ' sejam ',
              <span>descartadas</span>,
              ' Deseja continuar com esta ação?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
                history.push(
                  '/auth/coordinate-projects',
                  state.paginationIndex,
                );
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
              }}
            />
          </Overlay>
        );

      case 'removing':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>desalocando</span>,
              ' um funcionário e esta ação irá ',
              <span>retirá-lo</span>,
              ' deste projeto. Deseja prosseguir com esta ação? ',
            ]}
          >
            <UnfilledButton
              text="Sim"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
                overlayAction(removeEmployee);
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
              }}
            />
          </Overlay>
        );

      case 'returning':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>voltando para a tela anterior</span>,
              ' e esta ação irá ',
              <span>descartar</span>,
              ' as alterações que não foram salvas. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
                history.push(
                  '/auth/coordinate-projects',
                  state.paginationIndex,
                );
              }}
            />
            <FilledButton
              text="Não, permanecer"
              buttonClass="warn"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
              }}
            />
          </Overlay>
        );

      case 'confirm':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="success"
            text={[
              'Você está ',
              <span>alterando</span>,
              ' informações deste projeto. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim"
              handleClick={() => {
                overlayAction();
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="confirm"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(!overlayVisibility);
              }}
            />
          </Overlay>
        );

      default:
        return '';
    }
  };

  const submitForm = data => {
    const EditedObj = {
      id_cliente: data.clientID,
      id_status_projeto: data.projectStatus,
      coordenadores: data.technicalResponsible,
      id_acc_manager: data.accountManager,
      id_categoria_projeto: data.projectCategory,
      id_func_gp_smtx: data.projectManager,
      id_centro_de_custo: data.coustCenter,
      dt_inicio: data.realInitialDate,
      dt_final: data.realFinalDate,
      nome: data.projectName,
      responsavel: data.clientRepresentative || null,
    };

    // eslint-disable-next-line consistent-return
    const savedObj = _.mapKeys(state, (v, key) => {
      if (_.has(EditedObj, key)) {
        return key;
      }
    });

    delete savedObj.undefined;

    const diffObj = Object.keys(savedObj).reduce((diff, key) => {
      if (savedObj[key] === EditedObj[key]) return diff;
      return {
        ...diff,
        [key]: EditedObj[key],
      };
    }, {});

    const objectToSend = {
      ...diffObj,
      atendimento_ao_cliente: isClientAttendance ? 1 : 0,
      projeto_universal: isUniversal ? 1 : 0,
      id_usr_up: idFuncionario,
    };

    const send = () => {
      post(`${updateProject}/${state.id_projeto}`, objectToSend)
        .then(() =>
          renderToast('success', 'Alterações realizadas com sucesso', 5000),
        )
        .then(() => setFormAlterations(false))
        .catch(({ response }) => {
          const {
            data: { coordenadores },
          } = response;

          if (response.status === 500 && coordenadores) {
            renderToast(
              'error',
              'Coordenadores não podem possuir a mesma função',
              8000,
            );

            return coordenadores.map(
              ({ id_funcionario: id, nome_funcao: funcao }) =>
                setCoordinatorsList(currentValues =>
                  currentValues.map(coordinator => {
                    if (coordinator.id_funcionario_coordenador === id) {
                      return {
                        ...coordinator,
                        error: true,
                        funcao,
                      };
                    }

                    return coordinator;
                  }),
                ),
            );
          }

          return renderToast(
            'error',
            'Erro ao atualizar projeto. Tente novamente',
            8000,
          );
        });
    };

    return Object.keys(diffObj).length
      ? send()
      : renderToast(
          'information',
          'Não houveram alterações à serem salvas',
          8000,
        );
  };

  const handleAllocationDelete = id => {
    post(`${deleteAllocation}/${id}`)
      .then(() =>
        setEmployees(prev => prev.filter(item => item._id_alocacao !== id)),
      )
      .then(() =>
        renderToast('information', 'Funcionário desalocado com sucesso', 8000),
      )
      .then(() => setReloadEmployees(prev => !prev))
      .catch(() =>
        renderToast(
          'error',
          'Não foi possível desalocar o funcionário. Por favor, tente novamente',
          8000,
        ),
      );
  };

  const selectAnEmployee = data => {
    setModalOpen(true);
    setSelectedEmployee(data);
  };

  const listAllocations = param =>
    param.map(employee => {
      const {
        _id_alocacao: id,
        _nome: employeeName,
        _nome_funcao: employeeFunction,
        _nivel_senioridade: employeeLevel,
        _dt_inicio_alocacao: employeeInitialAllocation,
        _dt_final_alocacao: employeeFinalAllocation,
        _percentual: employeePercentAllocation,
      } = employee;

      const isAllocationOld =
        new Date(employeeFinalAllocation).getTime() <= new Date().getTime();

      return (
        <S.Employee
          key={employee._id_alocacao}
          className={isAllocationOld ? 'isOld' : ''}
        >
          <div>
            <p>{employeeName}</p>
            <p>
              {employeeFunction}
              <small>({employeeLevel})</small>
            </p>
            <div>
              <p>
                <span>Data de início:</span>{' '}
                {formatDate(employeeInitialAllocation)}
              </p>
              <p>
                <span>Data final:</span> {formatDate(employeeFinalAllocation)}
              </p>
            </div>
            <p>
              <span>Percentual: </span>{' '}
              {employeePercentAllocation.replace('.00', '%')}
            </p>
          </div>
          <S.EmployeeActions>
            {!isAllocationOld && (
              <button
                type="button"
                onClick={() => selectAnEmployee({ ...employee, idFuncionario })}
              >
                {actionSvgs('edit', '#0083CA')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setOverlayVisibility(true);
                setOverlayType('removing');
                setRemoveEmployee(id);
                setOverlayAction(() => handleAllocationDelete);
              }}
            >
              {actionSvgs('remove')}
            </button>
          </S.EmployeeActions>
        </S.Employee>
      );
    });

  return (
    !!Object.keys(selectOptions).length && (
      <>
        {overlayVisibility && renderOverlay(overlayType, overlayAction)}

        {toastVisibility && (
          <Toast
            type={toastType}
            message={toastMessage}
            autoClose={toastClose}
            hideProgress
          />
        )}

        {isLoading && (
          <S.Loader>
            <ContentLoader />
          </S.Loader>
        )}

        {modalOpen && (
          <EditAllocatedInfos
            isOpen={modalOpen}
            setIsOpen={setModalOpen}
            employeeData={selectedEmployee}
            reload={setReloadEmployees}
            renderToast={renderToast}
          />
        )}

        <S.Container>
          <MDBRow>
            <MDBCol lg="12">
              <ReturnPreviousPage
                text="Voltar para gerência de projetos"
                handleClick={() => {
                  const haveAlterations = () => {
                    setOverlayVisibility(true);
                    setOverlayType('returning');
                  };

                  const dontHaveAlterations = () => {
                    setToastVisibility(false);
                    history.push(
                      '/auth/coordinate-projects',
                      state.paginationIndex,
                    );
                  };

                  return formAlterations
                    ? haveAlterations()
                    : dontHaveAlterations();
                }}
              />
              <S.ProjectInfos>
                <S.FeaturedText>
                  {projectName || state.nome}
                  <span>({state.identificacao})</span>
                </S.FeaturedText>
                <FeaturedText text="Realize alterações e veja os funcionários alocados no projeto" />
              </S.ProjectInfos>
            </MDBCol>
          </MDBRow>
          <MDBRow>
            <MDBCol lg="6" sm="12">
              <S.Divider>
                Aqui você encontra as informações gerais do projeto
              </S.Divider>
              <Formik
                initialValues={initialValues}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={validationSchema}
                onSubmit={e => submitForm(e)}
              >
                {({ values, errors, handleSubmit, setFieldValue }) => (
                  <S.Form onSubmit={e => submitForm(e)} noValidate>
                    <InputField
                      labelText="Nome do projeto (*)"
                      placeholderText="Defina o nome do projeto"
                      value={values.projectName}
                      name="projectName"
                      handleChange={e => {
                        e.persist();
                        setFieldValue('projectName', e.target.value);
                        setProjectName(e.target.value);
                        setFormAlterations(true);
                      }}
                    />
                    {!!errors.projectName && (
                      <ErrorMessage message={errors.projectName} />
                    )}

                    <SelectField
                      options={sortArrayInAlphabeticalOrder(
                        selectOptions?.clients,
                        'label',
                      )}
                      value={selectOptions?.clients?.find(
                        option => option.value === values.clientID,
                      )}
                      handleChange={({ value }) => {
                        setFieldValue('clientID', value);
                        setFormAlterations(true);
                      }}
                      hasLabel
                      hasLabelMargin
                      labelText="Nome do cliente (*)"
                      placeholderText="Selecione o cliente favorecido"
                      name="clientID"
                    />
                    {!!errors.clientID && (
                      <ErrorMessage message={errors.clientID} />
                    )}

                    <S.CoordinatorContainer>
                      {coordinatorsList?.map((coordinator, index) => (
                        <S.CoordinatorBlock
                          className={coordinator.error ? 'error' : ''}
                        >
                          <SelectField
                            options={sortArrayInAlphabeticalOrder(
                              selectOptions?.technical,
                              'label',
                            )}
                            value={selectOptions?.technical?.find(
                              option =>
                                option.value ===
                                values.technicalResponsible[index]
                                  ?.id_funcionario,
                            )}
                            handleChange={({ value }) => {
                              const currentValues = values.technicalResponsible;
                              currentValues[index] = { id_funcionario: value };

                              setFieldValue('technicalResponsible', [
                                ...currentValues,
                              ]);
                              setFormAlterations(true);

                              return handleCoordinatorUpdate(
                                index,
                                value,
                                setCoordinatorsList,
                              );
                            }}
                            hasLabel
                            labelText={`${index + 1}º Responsável técnico`}
                            placeholderText="Selecione o responsável técnico"
                            name={`technicalResponsible-${index}`}
                          />
                          {coordinator.error && (
                            <ErrorMessage
                              message={`Pode existir apenas 1 ${coordinator.funcao} por projeto`}
                            />
                          )}

                          {!!errors.technicalResponsible && (
                            <ErrorMessage
                              message={errors.technicalResponsible}
                            />
                          )}

                          <S.RemoveCoordinator
                            onClick={() => {
                              const currentValues = values.technicalResponsible;
                              const removed = currentValues.filter(
                                ({ id_funcionario: id }) =>
                                  id !== coordinator.id_funcionario_coordenador,
                              );

                              setFieldValue('technicalResponsible', [
                                ...removed,
                              ]);
                              return handleCoordinatorDelete(
                                coordinator.id_funcionario_coordenador,
                                setCoordinatorsList,
                              );
                            }}
                            disabled={coordinatorsList?.length <= 1}
                          >
                            {actionSvgs('remove')}
                            Remover
                          </S.RemoveCoordinator>
                        </S.CoordinatorBlock>
                      ))}

                      <FilledButton
                        handleClick={() => {
                          const currentValues = values.technicalResponsible;
                          setFieldValue('technicalResponsible', [
                            ...currentValues,
                            { id_funcionario: -1 },
                          ]);

                          return handleCoordinatorInsert(setCoordinatorsList);
                        }}
                        text="+ Adicionar coodenador"
                      />
                    </S.CoordinatorContainer>
                    <SelectField
                      options={sortArrayInAlphabeticalOrder(
                        selectOptions?.projectManagers,
                        'label',
                      )}
                      value={selectOptions?.projectManagers?.find(
                        option => option.value === values.projectManager,
                      )}
                      handleChange={({ value }) => {
                        setFieldValue('projectManager', value);
                        setFormAlterations(true);
                      }}
                      hasLabel
                      hasLabelMargin
                      labelText="Gerente de projetos (*)"
                      placeholderText="Selecione o gerente de projetos"
                      name="projectManager"
                    />
                    {!!errors.projectManager && (
                      <ErrorMessage message={errors.projectManager} />
                    )}

                    <SelectField
                      options={sortArrayInAlphabeticalOrder(
                        selectOptions?.accountManagers,
                        'label',
                      )}
                      value={selectOptions?.accountManagers?.find(
                        option => option.value === values.accountManager,
                      )}
                      handleChange={({ value }) => {
                        setFieldValue('accountManager', value);
                        setFormAlterations(true);
                      }}
                      hasLabel
                      hasLabelMargin
                      labelText="Gerente de contas"
                      placeholderText="Selecione o gerente de contas"
                      name="accountManager"
                    />
                    {!!errors.accountManager && (
                      <ErrorMessage message={errors.accountManager} />
                    )}

                    <SelectField
                      options={sortArrayInAlphabeticalOrder(
                        selectOptions?.status,
                        'label',
                      )}
                      value={selectOptions?.status?.find(
                        option => option.value === values.projectStatus,
                      )}
                      handleChange={({ value }) => {
                        setFieldValue('projectStatus', value);
                        setFormAlterations(true);
                      }}
                      hasLabel
                      hasLabelMargin
                      labelText="Status do projeto (*)"
                      placeholderText="Selecione o status atual"
                      name="projectStatus"
                    />
                    {!!errors.projectStatus && (
                      <ErrorMessage message={errors.projectStatus} />
                    )}

                    <SelectField
                      labelText="Centro de custos (*)"
                      placeholderText="Selecione a localização..."
                      name="coustCenter"
                      options={sortArrayInAlphabeticalOrder(
                        selectOptions?.coustCenters,
                        'label',
                      )}
                      value={selectOptions?.coustCenters?.find(
                        option => option.value === values.coustCenter,
                      )}
                      hasLabel
                      hasLabelMargin
                      handleChange={({ value }) => {
                        setFieldValue('coustCenter', value);
                        setFormAlterations(true);
                      }}
                    />
                    {errors.coustCenter && (
                      <ErrorMessage message={errors.coustCenter} />
                    )}

                    <SelectField
                      options={sortArrayInAlphabeticalOrder(
                        selectOptions?.categories,
                        'label',
                      )}
                      value={selectOptions?.categories?.find(
                        option => option.value === values.projectCategory,
                      )}
                      handleChange={({ value }) => {
                        setFieldValue('projectCategory', value);
                        setFormAlterations(true);
                      }}
                      hasLabel
                      hasLabelMargin
                      labelText="Categoria do projeto (*)"
                      placeholderText="Selecione a categoria"
                      name="projectCategory"
                    />
                    {!!errors.projectCategory && (
                      <ErrorMessage message={errors.projectCategory} />
                    )}

                    <InputField
                      labelText="Representante do cliente"
                      placeholderText="Digite o nome do responsável"
                      value={values.clientRepresentative}
                      name="clientRepresentative"
                      handleChange={e => {
                        setFieldValue('clientRepresentative', e.target.value);
                        setFormAlterations(true);
                      }}
                    />
                    {!!errors.clientRepresentative && (
                      <ErrorMessage message={errors.clientRepresentative} />
                    )}

                    <S.FlexRow>
                      <div>
                        <DateField
                          handleChange={e => {
                            setFieldValue('expectedInitialDate', moment(e));
                            setFormAlterations(true);
                          }}
                          selectedValue={
                            new Date(moment(values.expectedInitialDate))
                          }
                          placeholderText="Insira a data"
                          onlyMonth={false}
                          name="expectedInitialDate"
                          labelText="Data inicial prevista"
                          disabled
                        />
                        {!!errors.expectedInitialDate && (
                          <ErrorMessage message={errors.expectedInitialDate} />
                        )}
                      </div>
                      <div>
                        <DateField
                          handleChange={e => {
                            setFieldValue('expectedFinalDate', moment(e));
                            setFormAlterations(true);
                          }}
                          selectedValue={
                            new Date(moment(values.expectedFinalDate))
                          }
                          placeholderText="Insira a data"
                          onlyMonth={false}
                          name="expectedFinalDate"
                          labelText="Data final prevista"
                          disabled
                        />
                        {!!errors.expectedFinalDate && (
                          <ErrorMessage message={errors.expectedFinalDate} />
                        )}
                      </div>
                    </S.FlexRow>
                    <S.FlexRow>
                      <div>
                        <DateField
                          handleChange={e => {
                            setFieldValue('realInitialDate', moment(e));
                            setFormAlterations(true);
                          }}
                          selectedValue={
                            new Date(moment(values.realInitialDate))
                          }
                          placeholderText="Insira a data"
                          onlyMonth={false}
                          name="realInitialDate"
                          labelText="Data inicial real"
                        />
                        {!!errors.realInitialDate && (
                          <ErrorMessage message={errors.realInitialDate} />
                        )}
                      </div>
                      <div>
                        <DateField
                          handleChange={e => {
                            setFieldValue('realFinalDate', moment(e));
                            setFormAlterations(true);
                          }}
                          selectedValue={new Date(moment(values.realFinalDate))}
                          placeholderText="Insira a data"
                          onlyMonth={false}
                          labelText="Data final real"
                          name="realFinalDate"
                        />
                        {!!errors.realFinalDate && (
                          <ErrorMessage message={errors.realFinalDate} />
                        )}
                      </div>
                    </S.FlexRow>
                    <S.FlexRow>
                      <Switch
                        id="isUniversal"
                        onChange={() => {
                          setFormAlterations(true);
                          setIsUniversal(current => !current);
                        }}
                        isChecked={isUniversal}
                        labelText="Projeto universal"
                      />
                      <Switch
                        id="isClientAttendance"
                        onChange={() => {
                          setFormAlterations(true);
                          setIsClientAttendance(current => !current);
                        }}
                        isChecked={isClientAttendance}
                        labelText="Atendimento ao cliente"
                      />
                    </S.FlexRow>
                    <S.Buttons>
                      <FilledButton
                        text="Cancelar"
                        buttonClass="cancel"
                        handleClick={() => {
                          setOverlayVisibility(true);
                          setOverlayType('cancel');
                        }}
                      />
                      <FilledButton
                        text="Salvar alterações"
                        buttonClass="confirm"
                        handleClick={() => {
                          setOverlayVisibility(true);
                          setOverlayType('confirm');
                          setOverlayAction(() => handleSubmit);
                        }}
                        disabled={!formAlterations}
                      />
                    </S.Buttons>
                  </S.Form>
                )}
              </Formik>
            </MDBCol>
          </MDBRow>
          <MDBRow>
            <MDBCol lg="6" sm="12">
              <S.Divider>
                Estes são os funcionários que estão alocados neste projeto
              </S.Divider>
              {isAdmin && (
                <S.AllocateProfessionals>
                  <S.AllocateButton
                    to={{
                      pathname:
                        '/auth/coordinate-projects/manage-project/allocate-professionals',
                      state: { ...state },
                    }}
                  >
                    + Alocar profissionais
                  </S.AllocateButton>
                </S.AllocateProfessionals>
              )}
              <S.Search>
                <FilterField
                  handleChange={e => {
                    filterEmployees();
                    setEmployeesSearched(e.target.value);
                  }}
                  value={employeesSearched}
                  name="filterEmployees"
                  labelText="Deseja pesquisar um funcionário em específico?"
                  placeholderText="Insira o nome do colaborador"
                  isTransparent
                />
              </S.Search>
              <Accordion
                accordionOpen={accordionOpen}
                setAccordionOpen={setAccordionOpen}
                callbackText="Não existem funcionários alocados neste projeto"
                headText="Funcionários alocados"
              >
                {employeesSearched.length > 0
                  ? listAllocations(filteredEmployees)
                  : listAllocations(employees)}
              </Accordion>
            </MDBCol>
          </MDBRow>
        </S.Container>
        <Footer />
      </>
    )
  );
}

export default ManageProject;

ManageProject.defaultProps = {
  history: {},
  location: {},
  user: {},
};

ManageProject.propTypes = {
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
